/**
 * API service for interacting with the sitemap indexer backend
 */

/**
 * Schedule indexing for site IDs
 * @param {string[]} siteIds - Array of site IDs to index
 * @returns {Promise<{success: string[], failed: string[]}>} - Response with successful and failed sites
 */
export const scheduleIndexing = async (siteIds) => {
    const endpoint = '/ddc-sitemap-indexer-api/manual';

    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ siteIds }),
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
        // Try to parse the error body
        let errorBody;
        try {
            errorBody = await response.json();
        } catch (e) {
            errorBody = { error: 'Unknown error occurred' };
        }

        const error = new Error(errorBody.error || 'Unknown error');
        error.status = response.status;
        error.originalError = errorBody;
        throw error;
    }

    return await response.json();
};

/**
 * Get indexing status for sites
 * @param {string[]} [siteIds] - Optional array of site IDs to get status for
 * @returns {Promise<Object>} - Response with site indexing status
 */
export const getIndexStatus = async (siteIds) => {
    let endpoint = '/ddc-sitemap-indexer-api/status';

    if (siteIds && siteIds.length > 0) {
        const siteIdsParam = siteIds.join(',');
        endpoint += `?siteIds=${encodeURIComponent(siteIdsParam)}`;
    }

    const requestOptions = {
        method: 'GET',
        headers: {
            'Cache-Control': 'no-cache',
        },
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
        let errorBody;
        try {
            errorBody = await response.json();
        } catch (e) {
            errorBody = { error: 'Unknown error occurred' };
        }

        const error = new Error(errorBody.error || 'Unknown error');
        error.status = response.status;
        error.originalError = errorBody;
        throw error;
    }

    return await response.json();
};

/**
 * Add demo sites to DynamoDB
 * @param {string[]} siteIds - Array of demo site IDs to add
 * @returns {Promise<{success: string[], failed: string[]}>} - Response with successful and failed sites
 */
export const addDemoSites = async (siteIds) => {
    const endpoint = '/ddc-sitemap-indexer-api/manual/demo';

    const requestOptions = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({ siteIds }),
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
        // Try to parse the error body
        let errorBody;
        try {
            errorBody = await response.json();
        } catch (e) {
            errorBody = { error: 'Unknown error occurred' };
        }

        const error = new Error(errorBody.error || 'Unknown error');
        error.status = response.status;
        error.originalError = errorBody;
        throw error;
    }

    return await response.json();
};

/**
 * Delete demo sites from DynamoDB
 * @param {string[]} siteIds - Array of demo site IDs to delete
 * @returns {Promise<{success: string[], failed: string[]}>} - Response with successful and failed sites
 */
export const deleteDemoSites = async (siteIds) => {
    let endpoint = '/ddc-sitemap-indexer-api/manual/demo';

    if (siteIds && siteIds.length > 0) {
        const siteIdsParam = siteIds.join(',');
        endpoint += `?siteIds=${encodeURIComponent(siteIdsParam)}`;
    }

    const requestOptions = {
        method: 'DELETE',
        headers: {
            'Cache-Control': 'no-cache',
        },
    };

    const response = await fetch(endpoint, requestOptions);

    if (!response.ok) {
        // Try to parse the error body
        let errorBody;
        try {
            errorBody = await response.json();
        } catch (e) {
            errorBody = { error: 'Unknown error occurred' };
        }

        const error = new Error(errorBody.error || 'Unknown error');
        error.status = response.status;
        error.originalError = errorBody;
        throw error;
    }

    return await response.json();
};
